import { FC } from 'react'
import { Box, FormControl, Grid, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent } from '@mui/material'
import { useQueryParams } from 'src/hooks/ui'
import { useBleConnectionLogFiltersRequest } from 'src/hooks/api'
import { SearchParamsKeys } from 'src/enums'

interface FilterSelectProps {
  label: string
  paramKey: string
  value: string
  options: { value: string; label: string }[]
  onChange: (paramKey: string, value: string) => void
}

const FilterSelect: FC<FilterSelectProps> = ({ label, paramKey, value, options, onChange }) => {
  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(paramKey, event.target.value)
  }

  return (
    <FormControl sx={{ mb: 1, width: 280 }}>
      <InputLabel id={`${paramKey}-label`}>{label}</InputLabel>
      <Select labelId={`${paramKey}-label`} id={`${paramKey}-select`} value={value} onChange={handleChange} input={<OutlinedInput id={`${paramKey}-input`} label={label} />}>
        <MenuItem value="">All</MenuItem>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export const BleConnectionLogListFilters: FC = () => {
  const [queryParams, setQueryParams, removeQueryParams] = useQueryParams()
  const { data: filters } = useBleConnectionLogFiltersRequest()

  const handleChange = (paramKey: string, value: string) => {
    if (!value) {
      removeQueryParams([paramKey, SearchParamsKeys.Page])
      return
    }
    setQueryParams({ [paramKey]: value, [SearchParamsKeys.Page]: '1' }, [paramKey, SearchParamsKeys.Page])
  }

  const contractorOptions = (filters?.contractors || []).map((contractor) => ({ value: `${contractor.id}`, label: contractor.name || `${contractor.id}` }))
  const projectOptions = (filters?.projects || []).map((project) => ({ value: `${project.id}`, label: project.name || `${project.id}` }))
  const eventTypeOptions = (filters?.eventTypes || []).map((eventType) => ({ value: eventType, label: eventType }))
  const probeOptions = (filters?.probeRawIds || []).map((probeRawId) => ({ value: probeRawId, label: probeRawId }))
  const appVersionOptions = (filters?.appVersions || []).map((appVersion) => ({ value: appVersion, label: appVersion }))
  const deviceOSOptions = (filters?.deviceOSes || []).map((deviceOS) => ({ value: deviceOS, label: deviceOS }))

  return (
    <Box>
      <Grid container alignItems="center" spacing={2} wrap="wrap">
        <Grid item>
          <FilterSelect
            label="Contractor"
            paramKey={SearchParamsKeys.Contractor}
            value={queryParams[SearchParamsKeys.Contractor] || ''}
            options={contractorOptions}
            onChange={handleChange}
          />
        </Grid>
        <Grid item>
          <FilterSelect
            label="Project"
            paramKey={SearchParamsKeys.ProjectId}
            value={queryParams[SearchParamsKeys.ProjectId] || ''}
            options={projectOptions}
            onChange={handleChange}
          />
        </Grid>
        <Grid item>
          <FilterSelect
            label="Event type"
            paramKey={SearchParamsKeys.EventType}
            value={queryParams[SearchParamsKeys.EventType] || ''}
            options={eventTypeOptions}
            onChange={handleChange}
          />
        </Grid>
        <Grid item>
          <FilterSelect
            label="RapidRH probe"
            paramKey={SearchParamsKeys.ProbeRawId}
            value={queryParams[SearchParamsKeys.ProbeRawId] || ''}
            options={probeOptions}
            onChange={handleChange}
          />
        </Grid>
        <Grid item>
          <FilterSelect
            label="App version"
            paramKey={SearchParamsKeys.AppVersion}
            value={queryParams[SearchParamsKeys.AppVersion] || ''}
            options={appVersionOptions}
            onChange={handleChange}
          />
        </Grid>
        <Grid item>
          <FilterSelect
            label="Device OS"
            paramKey={SearchParamsKeys.DeviceOS}
            value={queryParams[SearchParamsKeys.DeviceOS] || ''}
            options={deviceOSOptions}
            onChange={handleChange}
          />
        </Grid>
      </Grid>
    </Box>
  )
}
